/**
 * stockLedgerDataSource.ts — WatermelonDB → StockLedgerInput bridge.
 *
 * The signed delta of each movement comes from the canonical
 * `productStockDelta` (src/sync/liveStock) — the same function that derives
 * `product.currentStock` — so the ledger's balances reconcile to live stock.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Product, StockMovement } from '@/db/models/Inventory';
import User from '@/db/models/User';
import { productStockDelta } from '@/sync/liveStock';
import type { StockLedgerInput } from './stockLedger';

export interface StockLedgerResult {
  input: StockLedgerInput;
  productName: string;
  unit: string;
}

/** Load one product's movement ledger for `[from, to)` (plus later movements, to anchor the opening). */
export async function loadStockLedger(
  productId: string,
  from: Date,
  to: Date,
): Promise<StockLedgerResult | null> {
  const product = await database.get<Product>('products').find(productId).catch(() => null);
  if (!product) return null;

  const movements = await database.get<StockMovement>('stock_movements').query(
    Q.where('product_id', productId),
    Q.where('is_deleted', false),
    Q.where('created_at', Q.gte(from.getTime())),
  ).fetch();

  // Resolve staff names for attribution.
  const userIds = [...new Set(movements.map((m) => m.userId).filter((id): id is string => !!id))];
  const users = await fetchInChunks(userIds, (batch) =>
    database.get<User>('users').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const userName = new Map(users.map((u) => [u.id, u.name ?? u.phone ?? 'Unknown']));

  return {
    productName: product.name,
    unit: product.unit,
    input: {
      from,
      to,
      currentStock: product.currentStock,
      movements: movements.map((m) => ({
        date: m.createdAt,
        type: m.type,
        delta: productStockDelta(m.type, m.quantity),
        referenceType: m.referenceType,
        notes: m.notes,
        userName: m.userId ? userName.get(m.userId) ?? null : null,
      })),
    },
  };
}

export interface LedgerProductOption {
  id: string;
  name: string;
  sku: string | null;
  currentStock: number;
  unit: string;
}

/** Physical, inventory-tracked products for the ledger's product picker. */
export async function loadLedgerProducts(storeId: string): Promise<LedgerProductOption[]> {
  const products = await database.get<Product>('products').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('is_service', false),
    Q.where('track_inventory', true),
  ).fetch();

  return products
    .map((p) => ({ id: p.id, name: p.name, sku: p.sku, currentStock: p.currentStock, unit: p.unit }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
